import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Badge,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Label,
  Input,
  Textarea,
} from '@tonic/shared/components/ui';
import {
  ArrowLeft,
  Calendar,
  Thermometer,
  Package,
  History,
  Settings,
  Utensils,
  Clock,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { mockRefillContainers } from './mock-data';

function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'default';
    case 'in_use':
      return 'secondary';
    case 'maintenance':
      return 'destructive';
    default:
      return 'outline';
  }
}

function getConditionColor(condition) {
  switch (condition?.toLowerCase()) {
    case 'excellent':
      return 'default';
    case 'good':
      return 'secondary';
    case 'fair':
      return 'outline';
    case 'poor':
      return 'destructive';
    default:
      return 'outline';
  }
}

export function RefillDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState('details');
  const [maintenanceNote, setMaintenanceNote] = React.useState('');

  // Find the container
  const container = mockRefillContainers.find(c => c.id === id);

  const handleBackClick = () => {
    navigate('/admin/re-commerce/refill/containers');
  };

  const handleAddMaintenanceNote = () => {
    if (maintenanceNote.trim()) {
      console.log("Adding maintenance note:", maintenanceNote);
      setMaintenanceNote('');
    }
  };

  if (!container) {
    return (
      <div className="p-6">
        <Button variant="ghost" size="sm" onClick={handleBackClick}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Containers
        </Button>
        <div className="mt-6 text-center">
          <h2 className="text-xl font-semibold">Container Not Found</h2>
          <p className="text-muted-foreground mt-2">The requested container could not be found.</p>
        </div>
      </div>
    );
  }

  const isMeatballContainer = container.name.includes('HUVUDROLL');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBackClick}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Containers
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{container.name}</h1>
            <p className="text-sm text-muted-foreground">SKU: {container.sku}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={getConditionColor(container.condition)}>
            {container.condition}
          </Badge>
          <Badge variant={getStatusColor(container.status)}>
            {container.status}
          </Badge>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="customer">Customer</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Container Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Container ID</Label>
                  <p className="font-medium">{container.id}</p>
                </div>
                <div>
                  <Label>SKU</Label>
                  <p className="font-medium">{container.sku}</p>
                </div>
                <div>
                  <Label>Type</Label>
                  <p className="font-medium">{container.type}</p>
                </div>
                <div>
                  <Label>Capacity</Label>
                  <p className="font-medium">{container.capacity}</p>
                </div>
                <div>
                  <Label>Material</Label>
                  <p className="font-medium">{container.material}</p>
                </div>
                <div>
                  <Label>Total Refills</Label>
                  <p className="font-medium">{container.totalRefills}</p>
                </div>
              </div>

              {isMeatballContainer && (
                <Card className="bg-[#FFDB00] border-[#0058AB] border-2 mt-4">
                  <CardContent className="py-6">
                    <div className="flex items-center gap-4">
                      <Utensils className="w-12 h-12 text-[#0058AB]" />
                      <div>
                        <h3 className="text-lg font-bold text-[#0058AB]">HUVUDROLL Meatball Container</h3>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="flex items-center gap-2">
                            <Thermometer className="w-4 h-4 text-[#0058AB]" />
                            <div>
                              <Label className="text-[#0058AB]">Temperature</Label>
                              <p className="font-medium text-[#0058AB]">60°C</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-[#0058AB]" />
                            <div>
                              <Label className="text-[#0058AB]">Storage Time</Label>
                              <p className="font-medium text-[#0058AB]">4 hours remaining</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customer" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Current Customer</CardTitle>
            </CardHeader>
            <CardContent>
              {container.currentCustomer ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Name</Label>
                      <p className="font-medium">{container.currentCustomer.name}</p>
                    </div>
                    <div>
                      <Label>Email</Label>
                      <p className="font-medium">{container.currentCustomer.email}</p>
                    </div>
                  </div>
                  <div>
                    <Label>Subscription Type</Label>
                    <Badge variant="outline" className="mt-1">
                      {container.subscriptionType}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <div>
                      <Label>Last Refill</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span>{new Date(container.lastRefill).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div>
                      <Label>Next Refill</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span>{container.nextRefill ? new Date(container.nextRefill).toLocaleDateString() : 'Not scheduled'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">No customer currently assigned</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Refill History</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Refill history coming soon...</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="maintenance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Maintenance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Add a maintenance note..."
                  value={maintenanceNote}
                  onChange={(e) => setMaintenanceNote(e.target.value)}
                />
                <Button onClick={handleAddMaintenanceNote}>Add Note</Button>
              </div>
              <div className="mt-4">
                <p className="text-muted-foreground">Maintenance history coming soon...</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 
